import { Loader } from 'components';
import PopupWrapper from 'components/Popups/Wrapper';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIconBigger } from 'static';

import {
  ChoiceGrid,
  ChoiceOption,
  ChoiceOptionBody,
  ChoiceOptionTitle,
  ChoiceRadio,
  CloseButton,
  ContentArea,
  LoaderSlot,
  LoadingOverlay,
  LoadingText,
  ModalActions,
  ModalBtnPrimary,
  ModalBtnSecondary,
  ModalError,
  ModalFooter,
  ModalHeader,
  ModalPanel,
  ModalSubtitle,
  ModalTitle,
  ModeIconBadge,
  StepBody,
} from './SwitchBillingModeModal.styled';

export type PaymentMethodChoice = 'invoice' | 'card';

type ChoosePaymentMethodModalProps = {
  isOpen: boolean;
  selected: PaymentMethodChoice | null;
  submitting: boolean;
  redirecting: boolean;
  error: string | null;
  onClose: () => void;
  onSelect: (choice: PaymentMethodChoice) => void;
  onConfirm: () => void;
};

const panelMotion = {
  initial: { opacity: 0, scale: 0.96, y: 14 },
  animate: { opacity: 1, scale: 1, y: 0 },
  transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] as const },
};

const IconInvoice = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
  </svg>
);

const IconCard = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
  </svg>
);

const ChoosePaymentMethodModal = ({
  isOpen,
  selected,
  submitting,
  redirecting,
  error,
  onClose,
  onSelect,
  onConfirm,
}: ChoosePaymentMethodModalProps) => {
  const { t } = useTranslation();
  const busy = submitting || redirecting;
  const confirmLabel =
    selected === 'card'
      ? t('subscriptions.profile.payment.choosePaymentConfirmCard')
      : selected === 'invoice'
      ? t('subscriptions.profile.payment.choosePaymentConfirmInvoice')
      : t('subscriptions.profile.payment.choosePaymentConfirm');

  return (
    <PopupWrapper isOpen={isOpen}>
      <ModalPanel
        role="dialog"
        aria-modal="true"
        aria-labelledby="choose-payment-title"
        {...panelMotion}
      >
        <ContentArea>
          <ModalHeader>
            <ModalTitle id="choose-payment-title">
              {t('subscriptions.profile.payment.choosePaymentTitle')}
            </ModalTitle>
            <ModalSubtitle>
              {t('subscriptions.profile.payment.choosePaymentSubtitle')}
            </ModalSubtitle>
          </ModalHeader>

          <StepBody>
            <ChoiceGrid
              role="radiogroup"
              aria-label={t('subscriptions.profile.payment.choosePaymentTitle')}
            >
              <ChoiceOption
                type="button"
                role="radio"
                $selected={selected === 'invoice'}
                aria-checked={selected === 'invoice'}
                disabled={busy}
                onClick={() => onSelect('invoice')}
              >
                <ChoiceRadio $checked={selected === 'invoice'} />
                <ModeIconBadge $emphasis={selected === 'invoice'} aria-hidden>
                  <IconInvoice />
                </ModeIconBadge>
                <ChoiceOptionTitle>
                  {t('subscriptions.profile.payment.switchBillingInvoiceLabel')}
                </ChoiceOptionTitle>
                <ChoiceOptionBody>
                  {t('subscriptions.profile.payment.choosePaymentInvoiceBody')}
                </ChoiceOptionBody>
              </ChoiceOption>

              <ChoiceOption
                type="button"
                role="radio"
                $selected={selected === 'card'}
                aria-checked={selected === 'card'}
                disabled={busy}
                onClick={() => onSelect('card')}
              >
                <ChoiceRadio $checked={selected === 'card'} />
                <ModeIconBadge $emphasis={selected === 'card'} aria-hidden>
                  <IconCard />
                </ModeIconBadge>
                <ChoiceOptionTitle>
                  {t('subscriptions.profile.payment.switchBillingCardLabel')}
                </ChoiceOptionTitle>
                <ChoiceOptionBody>
                  {t('subscriptions.profile.payment.choosePaymentCardBody')}
                </ChoiceOptionBody>
              </ChoiceOption>
            </ChoiceGrid>
          </StepBody>

          <ModalFooter>
            {error ? <ModalError role="alert">{error}</ModalError> : null}

            <ModalActions>
              <ModalBtnSecondary
                type="button"
                onClick={onClose}
                disabled={busy}
              >
                {t('common.cancel')}
              </ModalBtnSecondary>
              <ModalBtnPrimary
                type="button"
                onClick={onConfirm}
                disabled={busy || !selected}
              >
                {confirmLabel}
              </ModalBtnPrimary>
            </ModalActions>
          </ModalFooter>
        </ContentArea>

        <CloseButton
          type="button"
          onClick={onClose}
          disabled={busy}
          aria-label={t('common.cancel')}
        >
          <CloseIconBigger />
        </CloseButton>

        {busy ? (
          <LoadingOverlay aria-live="polite" aria-busy="true">
            <LoaderSlot>
              <Loader size="56px" type={null} />
            </LoaderSlot>
            <LoadingText>
              {redirecting
                ? t('subscriptions.profile.payment.switchBillingRedirecting')
                : t('subscriptions.profile.payment.switchBillingProcessing')}
            </LoadingText>
          </LoadingOverlay>
        ) : null}
      </ModalPanel>
    </PopupWrapper>
  );
};

export default ChoosePaymentMethodModal;
